import { Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { CcfBox, CcfTypography } from '@nice-ccf/ui-controls';
import {
  getInitialTranscript,
  TranscriptMessage,
} from '@nice-ccf/ui-components';
import { useSelector } from 'react-redux';
import { generateDate } from '../../utils/utils';
import { CopilotVoiceTranscriptSection } from '../simulator-v2-copilot-tab/copilot-form-editor/CopilotVoiceTranscriptSection';

/* eslint-disable-next-line */
export interface SimulatorV2CallTranscriptTabProps {
  setUpdatedContact: any;
  updatedContact: any;
}

/**
 * used to customise the voice call transcript the agent sees after clicking "Show Transcript".
 * Until it is edited the default initialTranscript from the store is shown.
 * @param props - SimulatorV2CallTranscriptTabProps
 * @example ` <SimulatorV2CallTranscriptTab />`
 */
export function SimulatorV2CallTranscriptTab(
  props: SimulatorV2CallTranscriptTabProps
) {
  const { setUpdatedContact, updatedContact } = props;
  const defaultTranscript = useSelector(getInitialTranscript);
  const transcriptMessages: TranscriptMessage[] =
    updatedContact?.transcriptData ?? defaultTranscript;
  // Manual control unless auto run was chosen for this journey
  const playback: 'auto' | 'manual' =
    updatedContact?.transcriptPlayback === 'auto' ? 'auto' : 'manual';

  return (
    <CcfBox sx={{ width: '100%', pr: 1, pb: 2 }}>
      <CcfBox style={{ display: 'flex', flexDirection: 'column',margin:'10px',gap:'10px' }}>
        <CcfTypography variant="body2" sx={{ color: '#526b7a' }}>
          The conversation between the agent and the customer shown when the
          agent clicks Show Transcript on this call.{' '}
        </CcfTypography>
        <CcfTypography style={{ fontWeight: 700, color: '#d32f2f' }}>
          Each line is labelled automatically: agent lines with the logged-in
          agent (for example Agent Smith) and customer lines with this
          journey&apos;s customer.
        </CcfTypography>
      </CcfBox>
      <CcfBox sx={{ m: '10px', mb: 2 }}>
        <CcfTypography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Transcript playback
        </CcfTypography>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={playback}
          onChange={(_e, value) =>
            value &&
            setUpdatedContact((prev: any) => ({
              ...prev,
              lastUpdatedDate: generateDate(),
              transcriptPlayback: value,
            }))
          }
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2 } }}
        >
          <ToggleButton value="auto">Auto run</ToggleButton>
          <ToggleButton value="manual">Manual control</ToggleButton>
        </ToggleButtonGroup>
        <CcfTypography
          variant="body2"
          sx={{ color: '#526b7a', mt: 1, fontWeight: 600 }}
        >
          {playback === 'manual'
            ? 'The agent presses the Up arrow key to show each next message.'
            : 'Messages appear one by one automatically after Show Transcript is clicked.'}
        </CcfTypography>
      </CcfBox>
      <CopilotVoiceTranscriptSection
        messages={transcriptMessages}
        onChange={(msgs) =>
          setUpdatedContact((prev: any) => ({
            ...prev,
            lastUpdatedDate: generateDate(),
            transcriptData: msgs,
          }))
        }
      />
      <Button
        size="small"
        sx={{ textTransform: 'none', mt: 1 }}
        disabled={!updatedContact?.transcriptData}
        onClick={() =>
          setUpdatedContact((prev: any) => ({
            ...prev,
            lastUpdatedDate: generateDate(),
            transcriptData: undefined,
          }))
        }
      >
        Reset to default transcript
      </Button>
    </CcfBox>
  );
}

export default SimulatorV2CallTranscriptTab;
